<template>
  <v-content
    :class="{
      mobile: $vuetify.breakpoint.smAndDown,
      'single-entity': !multiEntity,
    }"
  >
    <v-container
      :style="showSideBar ? 'max-width:1200px' : 'max-width:1000px'"
      class="pt-0"
    >
      <div class="content">
        <!-- Partial: Navigation -->
        <div class="sticky-block">
          <sc-alert type="note" v-if="currentUser.readonly_access">
            This account is read-only. If you need to upload documents, please
            send us an email.
          </sc-alert>
          <sc-alert
            type="note"
            v-else-if="
              !isEmpty(currentCompany) && currentCompany.readonly_access
            "
          >
            This entity is read-only. If you need to upload documents, please
            send us an email.
          </sc-alert>

          <v-layout align-center>
            <soraban-action-button
              @click="$router.push('/admin-dashboard')"
              icon="keyboard_arrow_left"
              help="Back"
              v-if="isAdvisor"
            />
            <h3 class="text-xs-left" v-if="isAdvisor">
              You are currently viewing the client's dashboard
            </h3>
            <h2 class="text-xs-left" v-else>
              {{ $t('dashboard.welcome') }},
              <span class="ph-no-capture"
                >{{ currentUser.salutation_fname }}
                {{ currentUser.lname }}!</span
              >
            </h2>
          </v-layout>

          <v-tabs
            ref="tabs"
            class="tabs mt-0 pt-0"
            v-model="active"
            color="transparent"
            slider-color="primary"
            show-arrows
          >
            <v-tab class="text-capitalize" :ripple="false">{{
              $t('dashboard.dashboard')
            }}</v-tab>
            <v-tab
              class="text-capitalize"
              :ripple="false"
              v-if="!isFirmCommunicationDisabled"
            >
              {{ $t('dashboard.inbox') }}
              <v-badge class="mx-2" color="red" v-if="!isEmpty(unreadMessages)">
                <template v-slot:badge>
                  <span>{{ unreadMessages.length }}</span>
                </template>
              </v-badge>
            </v-tab>
            <v-tab
              class="text-capitalize"
              :ripple="false"
              v-if="!hideDocuments"
              >{{ $t('dashboard.documents') }}</v-tab
            >
            <v-tab class="text-capitalize" :ripple="false" v-if="!isAdvisor">{{
              $t('dashboard.profile')
            }}</v-tab>
            <v-tab class="text-capitalize hidden-md-and-up" :ripple="false">{{
              $t('dashboard.contactUs')
            }}</v-tab>
          </v-tabs>
        </div>

        <!-- Partial: Content -->
        <v-content class="ma-0 pt-4">
          <!-- Partial: Filter & List -->
          <v-layout column v-if="questionnaireTabActive">
            <v-layout v-if="shouldShowTaxYearFilter" class="align-center">
              <sc-h3 class="mr-3">{{ $t('dashboard.taxYear') }}:</sc-h3>
              <v-select
                v-model="yearFilter"
                solo
                hide-details
                :items="yearOptions"
                :placeholder="$t('dashboard.taxYear')"
                class="default-input year-input"
              />
            </v-layout>

            <questionnaire-list
              v-if="active === 0 && !isEmpty(currentCompany)"
              :current-company-id="currentCompanyId"
              :current-company="currentCompany"
              :questionnaire-id-to-open.sync="questionnaireIdToOpen"
              :year-filter="yearFilter"
              @openRelevantConversation="openRelevantConversation"
            />

            <tax-return-list
              v-if="active === 0 && !isEmpty(currentCompany)"
              :current-company="currentCompany"
              :year-filter="yearFilter"
            />
          </v-layout>

          <!-- Partial: Old Chat -->
          <Inbox
            v-if="inboxTabActive && !isEmpty(currentCompany) && !hasNewChat"
            :current-company-id="currentCompanyId"
            :current-company="currentCompany"
            :shouldFocusEditor.sync="shouldFocusEditor"
          />

          <!-- Partial: New Chat -->
          <InboxNew
            v-if="inboxTabActive && !isEmpty(currentCompany) && hasNewChat"
            :current-company-id="currentCompanyId"
            :current-company="currentCompany"
            :shouldFocusEditor.sync="shouldFocusEditor"
          />

          <documents
            v-if="documentsTabActive && !isEmpty(currentCompany)"
            :current-company-id="currentCompanyId"
            :current-company="currentCompany"
            @open-relevant-conversation="openRelevantConversation"
          />

          <profile
            v-if="profileTabActive && currentCompanyId && !isAdvisor"
            :current-company-id="currentCompanyId"
          />

          <v-layout
            class="hidden-md-and-up"
            v-if="contactUsTabActive && !isAdvisor"
          >
            <v-layout class="mt-4">
              <v-avatar
                :tile="true"
                :size="44"
                color="transparent"
                :class="{ avatar: !!currentUser.assigned_advisor_avatar }"
                v-if="
                  currentUser.assigned_advisor_avatar || currentFirm.thumb_url
                "
              >
                <img
                  :src="
                    currentUser.assigned_advisor_avatar
                      ? currentUser.assigned_advisor_avatar
                      : currentFirm.thumb_url
                  "
                  alt="avatar"
                  :class="{ logo: !!currentFirm.thumb_url }"
                />
              </v-avatar>

              <v-avatar
                :tile="true"
                :size="44"
                color="grey"
                class="avatar"
                v-else
              >
                <span class="white--text headline">{{
                  currentUser.fname[0].toUpperCase()
                }}</span>
              </v-avatar>

              <v-layout ml-2 column class="text-xs-left">
                <div class="advisor-name">
                  {{ currentUser.assigned_advisor_name }}
                </div>
              </v-layout>
            </v-layout>

            <div
              class="text-xs-left"
              v-if="
                !isFirmCommunicationDisabled ||
                  (currentFirm &&
                    ((currentFirm.preferences &&
                      currentFirm.preferences.show_phone_number &&
                      !isEmpty(currentFirm.preferences.phone_number)) ||
                      currentFirm.preferences.support_email))
              "
            >
              <div v-if="!isFirmCommunicationDisabled" class="pt-3">
                <span>{{ $t('dashboard.sendMeAMessage') }}</span>
              </div>
              <div
                v-if="
                  showCustomElement &&
                    showCustomElementFirst &&
                    currentCompany.custom_element
                "
                v-html="currentCompany.custom_element"
                class="mt-3"
              />
              <div
                v-if="
                  showCustomElement &&
                    showCustomElementFirst &&
                    defaultCustomElement
                "
                v-html="defaultCustomElement"
                class="mt-3"
              />
              <v-btn
                v-if="
                  !isFirmCommunicationDisabled && !currentUser.readonly_access
                "
                outline
                color="primary"
                block
                class="mt-3 mx-0 text-none"
                @click="goToInbox(true)"
              >
                <v-icon class="mr-2 material-icons-outlined">mail</v-icon>
                {{ $t('dashboard.sendAMessage') }}
              </v-btn>

              <v-btn
                outline
                color="primary"
                block
                class="mt-3 mx-0 text-none"
                v-if="
                  currentFirm &&
                    currentFirm.preferences &&
                    currentFirm.preferences.show_phone_number &&
                    !isEmpty(currentFirm.preferences.phone_number)
                "
                :href="'tel:' + currentFirm.preferences.phone_number"
              >
                <v-icon class="mr-2 material-icons-outlined">call</v-icon>
                {{ $t('dashboard.call') }}
                {{ currentFirm.preferences.phone_number }}
              </v-btn>

              <div
                v-if="
                  showCustomElement &&
                    !showCustomElementFirst &&
                    currentCompany.custom_element
                "
                v-html="currentCompany.custom_element"
                class="mt-3"
              />
              <div
                v-if="
                  showCustomElement &&
                    !showCustomElementFirst &&
                    defaultCustomElement
                "
                v-html="defaultCustomElement"
                class="mt-3"
              />
            </div>
          </v-layout>
        </v-content>
      </div>

      <!-- Partial: Sidebar -->
      <div class="sidebar hidden-sm-and-down" v-if="showSideBar">
        <v-layout>
          <v-avatar
            :tile="true"
            :size="44"
            color="transparent"
            :class="{ avatar: !!currentUser.assigned_advisor_avatar }"
            v-if="currentUser.assigned_advisor_avatar || currentFirm.thumb_url"
          >
            <img
              :src="
                currentUser.assigned_advisor_avatar
                  ? currentUser.assigned_advisor_avatar
                  : currentFirm.thumb_url
              "
              alt="avatar"
              class="logo"
            />
          </v-avatar>

          <v-avatar :tile="true" :size="44" color="grey" class="avatar" v-else>
            <span class="white--text headline">{{
              currentUser.fname[0].toUpperCase()
            }}</span>
          </v-avatar>

          <v-layout ml-2 column class="text-xs-left">
            <div class="advisor-name">
              {{ currentUser.assigned_advisor_name }}
            </div>
          </v-layout>
        </v-layout>

        <div class="text-xs-left">
          <div v-if="!isFirmCommunicationDisabled" class="pt-3">
            <span>{{ $t('dashboard.sendMeAMessage') }}</span>
          </div>
          <div
            v-if="
              showCustomElement &&
                showCustomElementFirst &&
                currentCompany.custom_element
            "
            v-html="currentCompany.custom_element"
            class="mt-3"
          />
          <div
            v-if="
              showCustomElement &&
                showCustomElementFirst &&
                defaultCustomElement
            "
            v-html="defaultCustomElement"
            class="mt-3"
          />
          <v-btn
            v-if="!isFirmCommunicationDisabled && !currentUser.readonly_access"
            outline
            color="primary"
            block
            class="mt-3 mx-0 text-none"
            @click="goToInbox(true)"
          >
            <v-icon class="mr-2 material-icons-outlined">mail</v-icon>
            {{ $t('dashboard.sendAMessage') }}
          </v-btn>

          <v-btn
            outline
            color="primary"
            block
            class="mt-3 mx-0 text-none"
            v-if="
              currentFirm &&
                currentFirm.preferences &&
                currentFirm.preferences.show_phone_number &&
                !isEmpty(currentFirm.preferences.phone_number)
            "
            :href="'tel:' + currentFirm.preferences.phone_number"
          >
            <v-icon class="mr-2 material-icons-outlined">call</v-icon>
            {{ $t('dashboard.call') }}
            {{ currentFirm.preferences.phone_number }}
          </v-btn>

          <div
            v-if="
              showCustomElement &&
                !showCustomElementFirst &&
                currentCompany.custom_element
            "
            v-html="currentCompany.custom_element"
            class="mt-3"
          />
          <div
            v-if="
              showCustomElement &&
                !showCustomElementFirst &&
                defaultCustomElement
            "
            v-html="defaultCustomElement"
            class="mt-3"
          />
        </div>
      </div>
    </v-container>
  </v-content>
</template>

<script>
import QuestionnaireList from '@/components/QuestionnaireList';
import TaxReturnList from '@/components/TaxReturnList';
import InboxNew from '@/components/InboxNew';
import Inbox from '@/components/Inbox';
import Documents from '@/components/Documents';
import Profile from '@/components/Profile';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { mapState, mapGetters } from 'vuex';
import API from '@/api';
import { yearOptions } from '@/constants/variables';
import FirmPreferenceMixins from '../mixins/FirmPreferenceMixins';

export default {
  components: {
    QuestionnaireList,
    TaxReturnList,
    Inbox,
    InboxNew,
    Documents,
    Profile,
  },

  mixins: [FirmPreferenceMixins],

  data() {
    return {
      previousTitle: '',
      active: 0,
      lastUnreadMessageId: null,
      shouldFocusEditor: false,
      currentCompanyId: null,
      currentCompany: {},
      questionnaireIdToOpen: null,
      yearFilter: 2024,
      yearOptions,
      currentYearStatus: null,
    };
  },

  methods: {
    get,
    isEmpty,

    openRelevantConversation() {
      this.$emit(
        'openSpecificConversation',
        this.currentCompany.relevant_conversation_id,
      );
    },

    goToInbox(focusEditor) {
      this.active = 1;
      this.shouldFocusEditor = focusEditor;
    },
  },

  computed: {
    ...mapGetters({
      isFirmCommunicationDisabled: 'isFirmCommunicationDisabled',
      isFirmDocumentsDisabled: 'isFirmDocumentsDisabled',
      isClientsDocumentsDisabled: 'isClientsDocumentsDisabled',
      isClientDashboardTaxYearFilterEnabled:
        'isClientDashboardTaxYearFilterEnabled',
      currentUser: 'user',
      currentFirm: 'currentFirm',
      defaultCompany: 'defaultCompany',
      myConversations: 'myConversations',
    }),
    ...mapGetters(['isAdvisor']),
    ...mapState('companies', ['companies']),
    ...mapState('questionnaires', ['taxYears']),

    shouldShowTaxYearFilter() {
      return (
        this.isClientDashboardTaxYearFilterEnabled ||
        (this.currentFirm &&
          this.currentFirm.preferences &&
          this.currentFirm.preferences.custom_statuses_enabled)
      );
    },
    showCustomElement() {
      return (
        this.customElementEnabled &&
        (this.currentCompany.custom_element || this.defaultCustomElement)
      );
    },
    hideDocuments() {
      return this.isClientsDocumentsDisabled || this.isFirmDocumentsDisabled;
    },
    showSideBar() {
      return (
        !this.isFirmCommunicationDisabled ||
        (this.currentFirm &&
          this.currentFirm.preferences.show_phone_number &&
          !isEmpty(this.currentFirm.preferences.phone_number)) ||
        (this.customElementEnabled && this.currentCompany.custom_element)
      );
    },
    multiEntity() {
      return this.currentCompanyId && !this.onlyIndividual;
    },
    currentEntityConversation() {
      return this.myConversations.find(
        c => c.id === this.currentCompany.relevant_conversation_id,
      );
    },
    unreadMessages() {
      const conversation = this.currentEntityConversation;

      if (conversation) {
        return conversation.unseen_messages.filter(message =>
          message.unseen_user_ids.includes(this.currentUser.id),
        );
      } else {
        return [];
      }
    },
    eitherConversationOrDocumentsDisabled() {
      return (
        (this.isFirmCommunicationDisabled && !this.hideDocuments) ||
        (this.hideDocuments && !this.isFirmCommunicationDisabled)
      );
    },

    questionnaireTabActive() {
      return this.active === 0;
    },

    inboxTabActive() {
      return this.active === 1 && !this.isFirmCommunicationDisabled;
    },

    documentsTabActive() {
      if (!this.hideDocuments) {
        return (
          (!this.isFirmCommunicationDisabled && this.active === 2) ||
          (this.isFirmCommunicationDisabled && this.active === 1)
        );
      } else {
        return false;
      }
    },

    profileTabActive() {
      return (
        this.active === 3 ||
        (this.eitherConversationOrDocumentsDisabled && this.active === 2) ||
        (this.isFirmCommunicationDisabled &&
          this.hideDocuments &&
          this.active === 1)
      );
    },

    contactUsTabActive() {
      return (
        this.active === 4 ||
        (this.eitherConversationOrDocumentsDisabled && this.active === 3) ||
        (this.isFirmCommunicationDisabled &&
          this.hideDocuments &&
          this.active === 2)
      );
    },

    onlyIndividual() {
      return (
        this.companies.length === 1 &&
        this.companies[0].entity_type === 'Individual'
      );
    },
  },

  beforeDestroy() {
    document.title = this.previousTitle;
  },
  async created() {
    // Make sure page is scrolled to top before anything happens
    setTimeout(() => globalThis.scrollTo(0, 0), 100);

    if (this.$route.query.companyId) {
      this.currentCompanyId = parseInt(this.$route.query.companyId);
    } else if (this.$route.params.hash) {
      const hash = this.$route.params.hash;
      const hashId = hash.substring(hash.lastIndexOf('-') + 1);
      try {
        const { data } = await API.company.getCompanyIdByHashId(hashId);

        this.currentCompanyId = data.id;
      } catch (error) {
        this.$router.push('/select-entity');
      }
    } else if (this.$route.query.conversationId) {
      this.currentCompanyId = this.defaultCompany.id;
      this.goToInbox(true);
    } else {
      this.currentCompanyId = this.defaultCompany.id;
    }

    if (this.$route.meta.tab === 'dashboard') {
      this.active = 0;
    } else if (
      this.$route.meta.tab === 'inbox' &&
      !this.isFirmCommunicationDisabled
    ) {
      this.active = 1;
    } else if (this.$route.meta.tab === 'documents') {
      if (this.isFirmCommunicationDisabled) {
        this.active = 1;
      } else {
        this.active = 2;
      }
    } else if (this.$route.meta.tab === 'profile') {
      if (this.isFirmCommunicationDisabled && this.hideDocuments) {
        this.active = 1;
      } else if (this.eitherConversationOrDocumentsDisabled) {
        this.active = 2;
      } else {
        this.active = 3;
      }
    }
    try {
      const response = await API.company.getCompany(this.currentCompanyId);
      this.currentCompany = response.data;
    } catch (e) {
      console.error(e);
      this.$router.push('/select-entity');
    }

    if (this.currentCompany.inactive) {
      this.$router.push('/select-entity');
    }

    this.previousTitle = document.title;
    document.title = this.currentCompany.name;

    if (this.$route.query.questionnaireId) {
      this.questionnaireIdToOpen = parseInt(this.$route.query.questionnaireId);
    }
  },

  watch: {
    taxYears(val) {
      if (val.length) {
        // this.yearFilter = val[0];
      }
    },
    $route(to, _from) {
      const isInboxRoute =
        to && to.fullPath && to.fullPath.startsWith('/inbox');

      if (isInboxRoute) {
        this.active = 1;
        this.$forceUpdate();
      }
    },
    active: {
      async handler(value) {
        if (this.currentCompany.name) {
          const hash =
            this.currentCompany.name
              .replaceAll(' ', '-')
              .replaceAll(',', '')
              .replaceAll('#', '') +
            '-' +
            this.currentCompany.hash_id;

          if (value === 0) {
            this.$router.push(`/dashboard/${hash}`);
          } else if (value === 1 && !this.isFirmCommunicationDisabled) {
            await this.$store.dispatch(
              'updateConversationAsSeen',
              this.currentEntityConversation.id,
            );
            this.$router.push(`/inbox/${hash}`);
          } else if (
            ((value === 1 && this.isFirmCommunicationDisabled) ||
              (value === 2 && !this.isFirmCommunicationDisabled)) &&
            !this.hideDocuments
          ) {
            this.$router.push(`/documents/${hash}`);
          } else if (
            (value === 1 &&
              this.isFirmCommunicationDisabled &&
              this.hideDocuments) ||
            (value === 2 && this.eitherConversationOrDocumentsDisabled) ||
            (value === 3 &&
              !this.isFirmCommunicationDisabled &&
              !this.hideDocuments)
          ) {
            this.$router.push(`/profile/${hash}`);
          }
        }
      },
    },

    'unreadMessages.length': {
      handler() {
        this.$refs.tabs.updateTabsView();
      },
    },
  },
};
</script>

<style lang="postcss" scoped>
.disable-action {
  cursor: default;
  box-shadow: none !important;
}

.disable-action:hover:before {
  background-color: initial;
}

.client-dashboard-wrapper {
  margin-top: 0 !important;
}
.container {
  display: flex;
  flex-direction: row;
  font-family: var(--font-family-sans);
}

.content {
  flex: auto;
  gap: 8px;
  min-width: 0;
}

.sidebar {
  margin-left: 24px;
  min-width: 320px;
  max-width: 320px;
  padding: 24px;

  max-height: 50vh;
  top: 64px;
  position: sticky;
}

.entity-input {
  & >>> .v-input__control {
    max-width: 250px;
  }
}

.sticky-block:after,
.sticky-block:before {
  position: absolute;
  top: 0;

  height: 92px;
  width: 24px;

  background-color: var(--gray-100);

  content: ' ';
}

.sticky-block:after {
  left: -24px;
}

.sticky-block:before {
  right: -24px;
}

.sticky-block {
  position: sticky;
  top: -36px;
  z-index: 1;

  margin-top: -36px;
  padding-top: 24px;

  background-color: var(--gray-100);
}

.client-dashboard-wrapper.mobile .sticky-block {
  top: 0;
  padding-bottom: 24px;
}

.tabs {
  & >>> .v-tabs__container {
    height: 36px;
  }

  & >>> .v-tabs__div {
    margin-right: 24px;
  }

  & >>> .v-tabs__item {
    padding: 8px 0px;
    font-weight: 500;
    font-size: 15px;

    .v-badge {
      margin-left: 3px;
      position: static;

      .v-badge__badge {
        position: static;
      }
    }
  }
}

.avatar {
  overflow: hidden;
  border-radius: 4px;
  box-shadow: 0 1px 1px rgba(0, 0, 0, 0.2);
}

.advisor-name {
  font-weight: bold;
  font-size: 15px;
}

.year-input {
  font-weight: bold;
  font-size: 18px;

  & >>> .v-input__slot {
    height: 48px;
    max-width: 200px;
    border: 1px solid #ddd;
    border-radius: 4px;
    box-shadow: 0px 1px 1px rgba(0 0 0 / 10%) !important;
  }
}
</style>
